/**
 * BoxDrawImage - Component that allows users to draw bounding boxes on an image using Canvas.
 *
 * Features:
 * - Click and drag to draw boxes (rendered on canvas)
 * - Drag to move existing boxes
 * - Delete/Backspace keys to delete boxes
 * - Zoom and pan support on image
 * - Pan/draw mode controls
 *
 * @example
 * <BoxDrawImage
 *   src="https://example.com/image.jpg"
 *   alt="My image"
 *   existingBoxes={[{ points: [[0.1, 0.1], [0.2, 0.1], [0.2, 0.2], [0.1, 0.2]] }]}
 *   enableZoom={true}
 *   onUserDrawnBoxesChange={(boxes) => console.log(boxes)}
 * />
 */
import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { Image, Box } from "@mantine/core";
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import { Point } from "../ImageWithBoundingBox";
import { ImageLoading } from "../ImageLoading";
import { BoxDrawImageProps, UserDrawnBox } from "./types";
import { getBoundingBox, getQuadPath, isPointInBox } from "./utils";
import { getLetterForIndex } from "../config/letterIndexMap";
import { BOX_COLORS } from "../styles/colors";
import { BoxDrawModeControls } from "./BoxDrawModeControls";

/**
 * Product hover tooltip. Rendered OUTSIDE the zoom TransformComponent (as a
 * child of the untransformed container) and positioned in that container's
 * screen coordinates, so it stays pinned to the cursor and at a constant size
 * regardless of zoom. `pos` is an offset relative to the container's top-left.
 */
const HoverTooltip = ({
  pos,
  info,
}: {
  pos: { x: number; y: number };
  info: { name: string; imageUrl?: string };
}) => (
  <div
    className="absolute z-[200] pointer-events-none bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 max-w-[200px]"
    style={{ left: pos.x + 12, top: pos.y - 10 }}
  >
    <div className="flex items-center gap-2">
      {info.imageUrl && (
        <img
          src={info.imageUrl}
          alt=""
          className="w-8 h-8 object-contain rounded flex-shrink-0"
        />
      )}
      <span className="text-xs font-medium text-gray-800 line-clamp-2">
        {info.name}
      </span>
    </div>
  </div>
);

export const BoxDrawImage: React.FC<BoxDrawImageProps> = ({
  src,
  alt = "Image",
  existingBoxes = [],
  hiddenExistingBoxIndices,
  selectedExistingBoxIndex,
  onSelectedExistingBoxChange,
  userDrawnBoxes: userDrawnBoxesProp = [],
  hiddenUserDrawnBoxIndices,
  onUserDrawnBoxesChange,
  selectedUserDrawnBoxIndex,
  onSelectedUserDrawnBoxChange,
  enableDrawing = true,
  enableDeletion = true,
  drawingBoxColor = BOX_COLORS.drawing.fill, // Pink for box being drawn
  userDrawnBoxColor = BOX_COLORS.userDrawn.fill, // Purple for user-drawn boxes
  existingBoxColor = BOX_COLORS.existing.fill, // Blue for existing boxes
  showModeControls = true,
  defaultMode = "pan",
  variant,
  crop = false,
  enableZoom = true,
  initialZoom = 1,
  minZoom = 1,
  holdKeysForZoom,
  zoomRef,
  boxImageOverlays,
  useNumbers = false,
  boxNumberLabels,
  confirmedBoxIndices,
  boxProductLabels,
  detectionBoxes = [],
  detectionBoxLabels,
  onDetectionHover,
  onDetectionClick,
  highlightableDetectionIndices,
  className = "",
  style = {},
}) => {
  const [mode, setMode] = useState<"pan" | "draw" | "draw-user">(defaultMode);

  // Sync internal mode when parent changes defaultMode
  useEffect(() => {
    setMode(defaultMode);
  }, [defaultMode]);

  const isDrawingMode = mode === "draw" || mode === "draw-user";
  const containerRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const internalTransformRef = useRef<ReactZoomPanPinchRef>(null);
  const transformRef = zoomRef ?? internalTransformRef;

  // Ref to always hold the latest drawBoxes so onLoad can call it without being a dep
  const drawBoxesRef = useRef<() => void>(() => {});

  // Cache for loaded overlay images
  const overlayImagesRef = useRef<Map<string, HTMLImageElement>>(new Map());

  // Load overlay images when boxImageOverlays changes
  useEffect(() => {
    if (!boxImageOverlays) return;
    for (const [, url] of boxImageOverlays) {
      if (!overlayImagesRef.current.has(url)) {
        const img = new window.Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          overlayImagesRef.current.set(url, img);
          drawBoxes();
        };
        img.src = url;
      }
    }
  }, [boxImageOverlays]);

  // Controlled component: parent owns the boxes state.
  // Use the prop directly for rendering, ref for event handler access.
  const boxes = userDrawnBoxesProp;
  const boxesRef = useRef(boxes);
  boxesRef.current = boxes;

  // Monotonically increasing counter for stable letter assignment.
  // Starts after the last existing box so user-drawn letters continue the sequence.
  // Never decremented on deletion — that's what keeps letters stable.
  const nextLetterIndexRef = useRef(existingBoxes.length);

  // Reset when the parent clears user-drawn boxes (new task loaded).
  useEffect(() => {
    if (userDrawnBoxesProp.length === 0) {
      nextLetterIndexRef.current = existingBoxes.length;
    }
  }, [userDrawnBoxesProp.length, existingBoxes.length]);

  // Stable refs for callbacks so event handlers don't need them as deps
  const onUserDrawnBoxesChangeRef = useRef(onUserDrawnBoxesChange);
  onUserDrawnBoxesChangeRef.current = onUserDrawnBoxesChange;
  const onSelectedUserDrawnBoxChangeRef = useRef(onSelectedUserDrawnBoxChange);
  onSelectedUserDrawnBoxChangeRef.current = onSelectedUserDrawnBoxChange;
  const onSelectedExistingBoxChangeRef = useRef(onSelectedExistingBoxChange);
  onSelectedExistingBoxChangeRef.current = onSelectedExistingBoxChange;
  const onDetectionHoverRef = useRef(onDetectionHover);
  onDetectionHoverRef.current = onDetectionHover;
  const onDetectionClickRef = useRef(onDetectionClick);
  onDetectionClickRef.current = onDetectionClick;

  // Update boxes via parent callback + optimistic ref update
  const updateBoxes = useCallback((newBoxes: UserDrawnBox[]) => {
    boxesRef.current = newBoxes;
    onUserDrawnBoxesChangeRef.current?.(newBoxes);
  }, []);

  const [selectedBoxIndexInternal, setSelectedBoxIndexInternal] = useState<
    number | null
  >(null);

  // Controlled selection: use prop if provided, otherwise internal state
  const selectedBoxIndex =
    selectedUserDrawnBoxIndex !== undefined
      ? selectedUserDrawnBoxIndex
      : selectedBoxIndexInternal;

  const setSelectedBoxIndex = useCallback((index: number | null) => {
    setSelectedBoxIndexInternal(index);
    onSelectedUserDrawnBoxChangeRef.current?.(index);
  }, []);

  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState<Point | null>(null);
  const [drawEnd, setDrawEnd] = useState<Point | null>(null);
  const [dragState, setDragState] = useState<{
    type: "move" | "resize";
    boxIndex: number;
    startPoint: Point;
    cornerIndex?: number;
    originalBox?: UserDrawnBox;
  } | null>(null);
  // Tracks the exact image URL that has finished loading. Loading is derived
  // from this (see below) rather than toggled via an effect + onLoad, which
  // raced on task transitions: the [src] effect could re-arm "loading" after
  // the image's load event already fired, leaving the spinner stuck.
  const [loadedSrc, setLoadedSrc] = useState<string | undefined>(undefined);
  const [hoveredHandle, setHoveredHandle] = useState<{
    boxIndex: number;
    cornerIndex: number;
  } | null>(null);
  const [hoveredBoxIndex, setHoveredBoxIndex] = useState<number | null>(null);
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(
    null,
  );
  // Hovered product-detection box (separate index space from existing/user boxes)
  const [hoveredDetectionIndex, setHoveredDetectionIndex] = useState<
    number | null
  >(null);
  const [detectionHoverPos, setDetectionHoverPos] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Combine existing boxes with user-drawn boxes for rendering
  const allBoxesForRendering = useMemo(() => {
    return [...existingBoxes, ...boxes];
  }, [existingBoxes, boxes]);

  const displaySrc = src;

  // Loading whenever there's an image to show but the URL currently being
  // rendered hasn't loaded yet (signing still in flight, or the image bytes
  // haven't arrived). Purely derived, so a task transition can't strand it.
  const isLoading = !!src && (!displaySrc || displaySrc !== loadedSrc);

  /**
   * Get transform state from TransformWrapper
   */
  const getTransformState = useCallback(() => {
    return transformRef.current?.instance.transformState || null;
  }, []);

  /**
   * Compute the rendered image rect inside a container under object-fit:contain.
   * Returns { offsetX, offsetY, width, height } of the actual visible image area.
   * Falls back to full container dimensions if the image isn't loaded yet.
   */
  const getRenderedImageRect = useCallback(
    (container: HTMLElement) => {
      const containerW = container.offsetWidth;
      const containerH = container.offsetHeight;
      if (crop)
        return {
          offsetX: 0,
          offsetY: 0,
          width: containerW,
          height: containerH,
        };

      const img = container.querySelector("img");
      if (!img || !img.naturalWidth || !img.naturalHeight) {
        return {
          offsetX: 0,
          offsetY: 0,
          width: containerW,
          height: containerH,
        };
      }

      const imgAspect = img.naturalWidth / img.naturalHeight;
      const containerAspect = containerW / containerH;

      let renderW: number, renderH: number;
      if (imgAspect > containerAspect) {
        // Image is wider than container — fits width, letterboxed top/bottom
        renderW = containerW;
        renderH = containerW / imgAspect;
      } else {
        // Image is taller than container — fits height, pillarboxed left/right
        renderH = containerH;
        renderW = containerH * imgAspect;
      }

      return {
        offsetX: (containerW - renderW) / 2,
        offsetY: (containerH - renderH) / 2,
        width: renderW,
        height: renderH,
      };
    },
    [crop],
  );

  /**
   * Convert screen coordinates to normalized image coordinates (0-1)
   * Accounts for zoom and pan transformations
   */
  const screenToNormalized = useCallback(
    (screenX: number, screenY: number): Point | null => {
      // Get the wrapper element (the untransformed container from TransformWrapper)
      // If wrapperRef is not available, fall back to imageContainerRef
      const wrapper = wrapperRef.current;
      const imageContainer = imageContainerRef.current;

      if (!wrapper && !imageContainer) return null;

      // Use wrapper for coordinate calculation if available (it's the untransformed container)
      // Otherwise use imageContainer (but this will be transformed, so we need to handle it differently)
      const container = wrapper || imageContainer;
      if (!container) return null;

      const rect = container.getBoundingClientRect();

      // Get relative position within container
      const relativeX = screenX - rect.left;
      const relativeY = screenY - rect.top;

      // Get the actual rendered image rect (accounts for object-fit: contain)
      const imgRect = getRenderedImageRect(container);

      // Try to account for transform if available
      const transformState = getTransformState();
      if (transformState && wrapper) {
        // We have the wrapper, so coordinates are relative to the untransformed wrapper
        const { scale, positionX, positionY } = transformState;

        // Convert from wrapper coordinates to content coordinates
        // The transform is: translate(positionX, positionY) scale(scale)
        // To reverse: contentX = (wrapperX - positionX) / scale
        const contentX = (relativeX - positionX) / scale;
        const contentY = (relativeY - positionY) / scale;

        // Normalize to 0-1 based on actual image rect (not full container)
        const normalizedX = (contentX - imgRect.offsetX) / imgRect.width;
        const normalizedY = (contentY - imgRect.offsetY) / imgRect.height;

        // Clamp to valid range
        return [
          Math.max(0, Math.min(1, normalizedX)),
          Math.max(0, Math.min(1, normalizedY)),
        ];
      }

      // Fallback: no transform or no wrapper - use simple normalization
      let normalizedX = (relativeX - imgRect.offsetX) / imgRect.width;
      let normalizedY = (relativeY - imgRect.offsetY) / imgRect.height;

      // Clamp to valid range
      normalizedX = Math.max(0, Math.min(1, normalizedX));
      normalizedY = Math.max(0, Math.min(1, normalizedY));

      return [normalizedX, normalizedY];
    },
    [getTransformState, getRenderedImageRect],
  );

  // ... inside the component

  /**
   * Draw all boxes on the canvas with handles
   */
  const drawBoxes = useCallback(() => {
    const canvas = canvasRef.current;
    // Use imageContainerRef for dimensions (excludes mode controls)
    const container = imageContainerRef.current || containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;

    // Get actual rendered image rect (accounts for object-fit: contain pillarboxing)
    const imgRect = getRenderedImageRect(container);
    const width = imgRect.width;
    const height = imgRect.height;
    const imgOffsetX = imgRect.offsetX;
    const imgOffsetY = imgRect.offsetY;

    // Get device pixel ratio for crisp rendering on high-DPI displays
    const dpr = window.devicePixelRatio || 1;

    // Get current zoom scale from transform for additional quality boost when zoomed
    const transformState = getTransformState();
    const zoomScale = transformState?.scale || 1;

    // Set canvas internal size accounting for DPR and zoom for high quality
    // Higher internal resolution = sharper rendering when zoomed
    const scaleFactor = dpr * Math.max(1, zoomScale);
    const canvasWidth = containerWidth * scaleFactor;
    const canvasHeight = containerHeight * scaleFactor;

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Scale CSS size back to display size (full container so it overlays correctly)
    canvas.style.width = `${containerWidth}px`;
    canvas.style.height = `${containerHeight}px`;

    // Scale context to account for DPR and zoom
    // This ensures crisp rendering at any zoom level
    ctx.scale(scaleFactor, scaleFactor);

    // Clear canvas (using display coordinates)
    ctx.clearRect(0, 0, containerWidth, containerHeight);

    // Draw all boxes (existing + user-drawn)
    allBoxesForRendering.forEach((box, boxIndex) => {
      if (!box.points.length) return;

      const isUserDrawnBox = boxIndex >= existingBoxes.length;
      const userDrawnBoxIndex = isUserDrawnBox
        ? boxIndex - existingBoxes.length
        : null;
      const isExistingBox = boxIndex < existingBoxes.length;

      // Skip hidden boxes
      if (isExistingBox && hiddenExistingBoxIndices?.has(boxIndex)) return;
      if (
        isUserDrawnBox &&
        userDrawnBoxIndex !== null &&
        hiddenUserDrawnBoxIndices?.has(userDrawnBoxIndex)
      )
        return;

      const bbox = getBoundingBox(box.points);
      // Check if this existing box is selected
      const isExistingBoxSelected =
        isExistingBox && selectedExistingBoxIndex === boxIndex;
      // Check if this user-drawn box is selected
      const isUserDrawnBoxSelected =
        isUserDrawnBox &&
        userDrawnBoxIndex !== null &&
        selectedBoxIndex === userDrawnBoxIndex;
      const isSelected = isExistingBoxSelected || isUserDrawnBoxSelected;

      // AABB retained for label placement/sizing; the fill/stroke follow the
      // true (possibly rotated) quad
      const x = imgOffsetX + bbox.minX * width;
      const y = imgOffsetY + bbox.minY * height;
      const boxWidth = (bbox.maxX - bbox.minX) * width;
      const boxHeight = (bbox.maxY - bbox.minY) * height;
      const boxPath =
        box.points.length >= 3 ? getQuadPath(box.points, imgRect) : null;

      // Draw fill - confirmed boxes get solid green, otherwise default colors
      const isConfirmedFill = confirmedBoxIndices?.has(boxIndex);
      if (isConfirmedFill) {
        ctx.fillStyle = "rgba(22, 163, 74, 0.35)"; // Solid green background for confirmed
      } else if (isExistingBox) {
        // Existing boxes
        ctx.fillStyle = isExistingBoxSelected
          ? BOX_COLORS.existing.fillSelected // Light blue when selected
          : existingBoxColor; // Blue for existing boxes
      } else {
        // User-drawn boxes
        ctx.fillStyle = isUserDrawnBoxSelected
          ? drawingBoxColor // Pink when selected (just drawn)
          : userDrawnBoxColor; // Purple when not selected
      }
      if (boxPath) {
        ctx.fill(boxPath);
      } else {
        ctx.fillRect(x, y, boxWidth, boxHeight);
      }

      // Determine border color — green for confirmed selections, otherwise default
      const isConfirmed = confirmedBoxIndices?.has(boxIndex);
      const borderColor = isConfirmed
        ? "#16a34a" // Green for confirmed product selection
        : isExistingBox
          ? BOX_COLORS.existing.border
          : isUserDrawnBoxSelected
            ? BOX_COLORS.drawing.border
            : BOX_COLORS.userDrawn.border;

      // Draw border
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = isSelected || isConfirmed ? 3 : 2;
      if (boxPath) {
        ctx.stroke(boxPath);
      } else {
        ctx.strokeRect(x, y, boxWidth, boxHeight);
      }

      // Draw label — numbers (1-indexed) or letters
      const label = useNumbers
        ? String(boxNumberLabels?.get(boxIndex) ?? boxIndex + 1)
        : isUserDrawnBox && (box as UserDrawnBox).letter
          ? (box as UserDrawnBox).letter!
          : getLetterForIndex(boxIndex);
      if (label) {
        ctx.fillStyle = "white";
        const fontSize = Math.max(12, Math.min(boxWidth, boxHeight) / 2);
        ctx.font = `bold ${fontSize}px sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(label, x + boxWidth / 2, y + boxHeight / 2);
      }

      // Draw handles if selected
      if (isSelected) {
        // Handle size in display pixels (constant regardless of zoom)
        // The canvas context is already scaled for quality, so we use constant size
        const handleSize = 8;
        const handleRadius = handleSize / 2;

        // Draw corner handles
        box.points.forEach((point) => {
          const px = imgOffsetX + point[0] * width;
          const py = imgOffsetY + point[1] * height;
          ctx.fillStyle = BOX_COLORS.handles.fill;
          ctx.strokeStyle = BOX_COLORS.handles.stroke;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(px, py, handleRadius, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        });
      }
    });

    // Draw ONLY the product-detection box currently under the cursor — the
    // rest stay hidden to keep the shelf image uncluttered. Skipped when the
    // hovered detection doesn't resolve to a product in the current planogram,
    // so a shown box always corresponds to a planogram highlight.
    if (hoveredDetectionIndex !== null) {
      const box = detectionBoxes[hoveredDetectionIndex];
      const isHighlightable =
        !highlightableDetectionIndices ||
        highlightableDetectionIndices.has(hoveredDetectionIndex);
      if (box?.points.length && isHighlightable) {
        // Solid white outline with a soft white glow, no fill.
        ctx.save();
        ctx.strokeStyle = BOX_COLORS.detection.border;
        ctx.lineWidth = 2;
        ctx.shadowColor = BOX_COLORS.detection.glow;
        ctx.shadowBlur = 8;
        if (box.points.length >= 3) {
          ctx.stroke(getQuadPath(box.points, imgRect));
        } else {
          const bbox = getBoundingBox(box.points);
          ctx.strokeRect(
            imgOffsetX + bbox.minX * width,
            imgOffsetY + bbox.minY * height,
            (bbox.maxX - bbox.minX) * width,
            (bbox.maxY - bbox.minY) * height,
          );
        }
        ctx.restore();
      }
    }

    // Draw current drawing box if active
    if (isDrawing && drawStart && drawEnd) {
      const minX = Math.min(drawStart[0], drawEnd[0]);
      const maxX = Math.max(drawStart[0], drawEnd[0]);
      const minY = Math.min(drawStart[1], drawEnd[1]);
      const maxY = Math.max(drawStart[1], drawEnd[1]);

      const x = imgOffsetX + minX * width;
      const y = imgOffsetY + minY * height;
      const boxWidth = (maxX - minX) * width;
      const boxHeight = (maxY - minY) * height;

      // Draw fill
      ctx.fillStyle = drawingBoxColor;
      ctx.fillRect(x, y, boxWidth, boxHeight);

      // Draw border - pink for drawing box
      ctx.strokeStyle = BOX_COLORS.drawing.border;
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, boxWidth, boxHeight);
    }
  }, [
    boxes,
    isDrawing,
    drawStart,
    drawEnd,
    drawingBoxColor,
    userDrawnBoxColor,
    existingBoxColor,
    selectedBoxIndex,
    getBoundingBox,
    allBoxesForRendering,
    existingBoxes,
    hiddenExistingBoxIndices,
    hiddenUserDrawnBoxIndices,
    selectedExistingBoxIndex,
    variant,
    getTransformState,
    getRenderedImageRect,
    boxImageOverlays,
    useNumbers,
    boxNumberLabels,
    confirmedBoxIndices,
    detectionBoxes,
    hoveredDetectionIndex,
    highlightableDetectionIndices,
  ]);

  // Keep the ref in sync with the latest drawBoxes
  drawBoxesRef.current = drawBoxes;

  // Redraw canvas when boxes or drawing state changes
  useEffect(() => {
    drawBoxes();
  }, [drawBoxes]);

  // Throttled version of drawBoxes for onTransformed callback
  // Use requestAnimationFrame to throttle canvas redraws during transforms
  // TODO do something with this
  const drawBoxesThrottledRef = useRef<number | null>(null);
  const handleTransformed = useCallback(() => {
    if (drawBoxesThrottledRef.current !== null) {
      cancelAnimationFrame(drawBoxesThrottledRef.current);
    }
    drawBoxesThrottledRef.current = requestAnimationFrame(() => {
      drawBoxes();
      drawBoxesThrottledRef.current = null;
    });
  }, [drawBoxes]);

  /**
   * Find which box (existing or user-drawn) contains the point
   */
  const findBoxAtPoint = useCallback(
    (point: Point): { boxIndex: number; isUserDrawn: boolean } | null => {
      // Check user-drawn boxes first (they're on top), skip hidden
      for (let i = boxes.length - 1; i >= 0; i--) {
        if (hiddenUserDrawnBoxIndices?.has(i)) continue;
        const box = boxes[i];
        if (box && isPointInBox(point, box)) {
          return { boxIndex: i, isUserDrawn: true };
        }
      }
      // Then check existing boxes, skip hidden
      for (let i = existingBoxes.length - 1; i >= 0; i--) {
        if (hiddenExistingBoxIndices?.has(i)) continue;
        const box = existingBoxes[i];
        if (box && isPointInBox(point, box)) {
          return { boxIndex: i, isUserDrawn: false };
        }
      }
      return null;
    },
    [
      boxes,
      existingBoxes,
      hiddenExistingBoxIndices,
      hiddenUserDrawnBoxIndices,
      isPointInBox,
    ],
  );

  /**
   * Find which product-detection box the point is "over".
   *
   * Hit-testing stays on the axis-aligned bounds even though detections are
   * drawn as their true (possibly rotated) quads: tilted quads overlap heavily
   * when the camera is angled, and a naive point-in-polygon test leaves dead
   * zones in the overlap that belong to neither box, while a "top-most
   * containing" pick is arbitrary there. Slight early activation just outside
   * a rotated quad's corner is acceptable. Instead:
   *   1. Candidate = any detection whose axis-aligned bounds contain the point
   *      (no corner dead zones).
   *   2. Among candidates, pick the one whose center is nearest the cursor, so
   *      the overlap region splits down the middle and both neighbors stay
   *      reachable.
   */
  const findDetectionAtPoint = useCallback(
    (point: Point): number | null => {
      let best: number | null = null;
      let bestDist = Infinity;
      for (let i = 0; i < detectionBoxes.length; i++) {
        const box = detectionBoxes[i];
        if (!box?.points.length) continue;
        const bb = getBoundingBox(box.points);
        if (
          point[0] < bb.minX ||
          point[0] > bb.maxX ||
          point[1] < bb.minY ||
          point[1] > bb.maxY
        )
          continue;
        const cx = (bb.minX + bb.maxX) / 2;
        const cy = (bb.minY + bb.maxY) / 2;
        const dist = (point[0] - cx) ** 2 + (point[1] - cy) ** 2;
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      return best;
    },
    [detectionBoxes, getBoundingBox],
  );

  /**
   * Check if point is near a corner handle of a selected box
   * Returns the corner index (0-3) if near a handle, null otherwise
   */
  const findHandleAtPoint = useCallback(
    (point: Point, boxIndex: number | null): number | null => {
      if (boxIndex === null || boxIndex >= boxes.length) return null;

      const box = boxes[boxIndex];
      if (!box || !box.points.length) return null;

      // Handle size in normalized coordinates (approximate)
      // We'll use a threshold based on screen pixels converted to normalized
      const container = imageContainerRef.current || containerRef.current;
      if (!container) return null;

      const imgRect = getRenderedImageRect(container);

      // Handle radius is 4px (constant), threshold is ~2x for easier clicking
      const handleRadius = 4;
      const threshold =
        (handleRadius * 2) / Math.min(imgRect.width, imgRect.height);

      // Check each corner
      for (let i = 0; i < box.points.length; i++) {
        const corner = box.points[i];
        const dx = point[0] - corner[0];
        const dy = point[1] - corner[1];
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < threshold) {
          return i;
        }
      }

      return null;
    },
    [boxes, getTransformState, getRenderedImageRect],
  );

  /**
   * Handle mouse down event
   */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const point = screenToNormalized(e.clientX, e.clientY);
      if (!point) return;

      // First check if clicking on a corner handle for resizing.
      // Gated on enableDrawing so read-only contexts (admin review, and the
      // product-selection flow) that render this layer only for detection
      // hover/click can't mutate box geometry.
      if (enableDrawing && selectedBoxIndex !== null) {
        const handleIndex = findHandleAtPoint(point, selectedBoxIndex);
        if (handleIndex !== null) {
          e.preventDefault();
          e.stopPropagation();
          const box = boxesRef.current[selectedBoxIndex];
          setDragState({
            type: "resize",
            boxIndex: selectedBoxIndex,
            startPoint: point,
            cornerIndex: handleIndex,
            originalBox: box ? { ...box, points: [...box.points] } : undefined,
          });
          return;
        }
      }

      // Check if clicking on a box (for moving) - only user-drawn boxes can be moved
      const boxAtPoint = findBoxAtPoint(point);
      if (boxAtPoint) {
        if (boxAtPoint.isUserDrawn) {
          // Prevent default to block panning when dragging a box
          e.preventDefault();
          e.stopPropagation();
          setSelectedBoxIndex(boxAtPoint.boxIndex);
          // Clear existing box selection when selecting user-drawn box
          onSelectedExistingBoxChangeRef.current?.(null);
          // Only start a move-drag when editing is enabled; when disabled
          // (admin review / selection flow) selection is fine but geometry
          // stays locked.
          if (enableDrawing) {
            setDragState({
              type: "move",
              boxIndex: boxAtPoint.boxIndex,
              startPoint: point,
            });
          }
          return;
        } else {
          // Clicked on an existing box - just select it
          e.preventDefault();
          e.stopPropagation();
          onSelectedExistingBoxChangeRef.current?.(boxAtPoint.boxIndex);
          setSelectedBoxIndex(null);
          return;
        }
      }

      // Product-detection click (read-only). Fires whenever a draw won't start
      // (pan mode, or drawing disabled — e.g. the selection flow leaves a stale
      // "draw" mode) and no editable box was hit above — empty-area/user boxes
      // always win. Never mutates selection or drawing state.
      if (!(enableDrawing && isDrawingMode) && onDetectionClickRef.current) {
        const detectionIndex = findDetectionAtPoint(point);
        if (detectionIndex !== null) {
          e.preventDefault();
          e.stopPropagation();
          onDetectionClickRef.current(detectionIndex);
          return;
        }
      }

      // If not clicking on a box and drawing mode is active, start drawing
      if (enableDrawing && isDrawingMode) {
        // Prevent default to block panning when drawing
        e.preventDefault();
        e.stopPropagation();
        setSelectedBoxIndex(null);
        onSelectedExistingBoxChangeRef.current?.(null);
        setIsDrawing(true);
        setDrawStart(point);
        setDrawEnd(point);
      } else {
        // Not on a box and in pan mode - let panning work (don't prevent default)
        setSelectedBoxIndex(null);
        onSelectedExistingBoxChangeRef.current?.(null);
      }
    },
    [
      enableDrawing,
      screenToNormalized,
      findBoxAtPoint,
      findDetectionAtPoint,
      findHandleAtPoint,
      isDrawingMode,
      selectedBoxIndex,
    ],
  );

  /**
   * Handle mouse move event
   */
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const point = screenToNormalized(e.clientX, e.clientY);
      if (!point) return;

      // Handle dragging
      if (dragState) {
        e.preventDefault();
        e.stopPropagation();

        if (dragState.type === "move") {
          // Move box
          const deltaX = point[0] - dragState.startPoint[0];
          const deltaY = point[1] - dragState.startPoint[1];

          const newBoxes = [...boxesRef.current];
          const box = newBoxes[dragState.boxIndex];
          if (box) {
            newBoxes[dragState.boxIndex] = {
              ...box,
              points: box.points.map(
                (p) => [p[0] + deltaX, p[1] + deltaY] as Point,
              ),
            };
          }
          updateBoxes(newBoxes);

          // Update drag state with new start point
          setDragState({
            ...dragState,
            startPoint: point,
          });
        } else if (
          dragState.type === "resize" &&
          dragState.cornerIndex !== undefined &&
          dragState.originalBox
        ) {
          // Resize box by moving the corner
          // Box points are: [top-left, top-right, bottom-right, bottom-left]
          // Assumes an axis-aligned rectangle — fine, since only user-drawn
          // boxes (always axis-aligned) are resizable; existing detection
          // boxes can be rotated quads but are select-only
          const deltaX = point[0] - dragState.startPoint[0];
          const deltaY = point[1] - dragState.startPoint[1];
          const cornerIndex = dragState.cornerIndex;

          const newBoxes = [...boxesRef.current];
          const box = newBoxes[dragState.boxIndex];
          if (box && dragState.originalBox) {
            const originalPoints = dragState.originalBox.points;
            const newPoints = [...originalPoints];

            // Calculate new position for the dragged corner
            const newCornerX = originalPoints[cornerIndex][0] + deltaX;
            const newCornerY = originalPoints[cornerIndex][1] + deltaY;

            // Update the dragged corner
            newPoints[cornerIndex] = [newCornerX, newCornerY] as Point;

            // Update adjacent corners to maintain rectangle shape
            // Corner indices: 0=top-left, 1=top-right, 2=bottom-right, 3=bottom-left
            if (cornerIndex === 0) {
              newPoints[1] = [originalPoints[1][0], newCornerY] as Point;
              newPoints[3] = [newCornerX, originalPoints[3][1]] as Point;
            } else if (cornerIndex === 1) {
              newPoints[0] = [originalPoints[0][0], newCornerY] as Point;
              newPoints[2] = [newCornerX, originalPoints[2][1]] as Point;
            } else if (cornerIndex === 2) {
              newPoints[3] = [originalPoints[3][0], newCornerY] as Point;
              newPoints[1] = [newCornerX, originalPoints[1][1]] as Point;
            } else if (cornerIndex === 3) {
              newPoints[2] = [originalPoints[2][0], newCornerY] as Point;
              newPoints[0] = [newCornerX, originalPoints[0][1]] as Point;
            }

            newBoxes[dragState.boxIndex] = {
              ...box,
              points: newPoints,
            };
          }
          updateBoxes(newBoxes);
        }
        return;
      }

      // Handle drawing
      if (isDrawing && drawStart) {
        e.preventDefault();
        e.stopPropagation();
        setDrawEnd(point);
      }
    },
    [dragState, isDrawing, drawStart, screenToNormalized],
  );

  /**
   * Handle mouse up event
   */
  const handleMouseUp = useCallback(() => {
    // Finish dragging
    if (dragState) {
      setDragState(null);
      return;
    }

    // Finish drawing
    if (!isDrawing || !drawStart || !drawEnd) return;

    // Calculate box bounds
    const minX = Math.min(drawStart[0], drawEnd[0]);
    const maxX = Math.max(drawStart[0], drawEnd[0]);
    const minY = Math.min(drawStart[1], drawEnd[1]);
    const maxY = Math.max(drawStart[1], drawEnd[1]);

    // Only create box if it has meaningful size
    const width = maxX - minX;
    const height = maxY - minY;
    if (width > 0.01 && height > 0.01) {
      // Create box with 4 corner points
      const newBox: UserDrawnBox = {
        id: crypto.randomUUID(),
        points: [
          [minX, minY], // top-left
          [maxX, minY], // top-right
          [maxX, maxY], // bottom-right
          [minX, maxY], // bottom-left
        ],
        type: mode === "draw-user" ? "draw-user" : "draw",
        letter: getLetterForIndex(nextLetterIndexRef.current++),
      };

      const currentBoxes = boxesRef.current;
      setSelectedBoxIndex(currentBoxes.length); // Select the new box
      updateBoxes([...currentBoxes, newBox]);
    }

    setIsDrawing(false);
    setDrawStart(null);
    setDrawEnd(null);
  }, [isDrawing, drawStart, drawEnd, dragState, mode]);

  // Handle keyboard deletion
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Delete" || e.key === "Backspace") {
        const currentBoxes = boxesRef.current;
        if (selectedBoxIndex !== null) {
          updateBoxes(currentBoxes.filter((_, i) => i !== selectedBoxIndex));
          setSelectedBoxIndex(null);
        } else if (currentBoxes.length > 0) {
          updateBoxes(currentBoxes.slice(0, -1));
        }
      }
    };

    if (enableDeletion) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [enableDeletion, selectedBoxIndex, updateBoxes]);

  // TODO do something with this
  const handleInit = useCallback((ref: ReactZoomPanPinchRef) => {
    if (ref.instance?.wrapperComponent) {
      wrapperRef.current = ref.instance.wrapperComponent;
    }
  }, []);

  const handleSetMode = useCallback((mode: "pan" | "draw" | "draw-user") => {
    setMode(mode);
  }, []);

  // Memoize ImageContainer to prevent unnecessary re-renders
  const imageContainer = useMemo(() => {
    // Determine cursor based on state and mode
    let cursor = "default";
    if (dragState?.type === "resize") {
      cursor = "nwse-resize"; // Resize cursor
    } else if (dragState?.type === "move") {
      cursor = "grabbing";
    } else if (hoveredHandle) {
      cursor = "nwse-resize"; // Resize cursor when hovering over handle
    } else if (isDrawing || isDrawingMode) {
      cursor = "crosshair"; // Drawing mode or actively drawing
    } else {
      cursor = "grab"; // Pan mode
    }

    return (
      <div
        ref={imageContainerRef}
        className="relative w-full h-full pointer-events-none"
      >
        {displaySrc && (
          <Image
            key={displaySrc}
            src={displaySrc}
            alt={alt}
            radius="md"
            className="relative border border-gray-200 shadow-sm w-full h-full"
            style={{
              objectFit: crop ? "cover" : "contain",
            }}
            onLoad={() => {
              setLoadedSrc(displaySrc);
              requestAnimationFrame(() => drawBoxesRef.current());
            }}
            onError={() => setLoadedSrc(displaySrc)}
          />
        )}
        {/* Canvas for drawing - always visible but doesn't block events */}
        <div
          className="absolute top-0 left-0 w-full h-full z-[100]"
          style={{ top: 0 }}
        >
          <canvas
            ref={canvasRef}
            className=""
            style={{
              cursor,
              pointerEvents: "none",
            }}
          />
        </div>
        {/* Event handler layer - captures mouse events for boxes/drawing, allows panning when not on box.
            Also renders when there are product detections, so their hover/click work even
            with drawing/deletion disabled (e.g. during the OOS product-selection flow). */}
        {(enableDrawing ||
          enableDeletion ||
          isDrawingMode ||
          detectionBoxes.length > 0) && (
          <div
            className="absolute top-0 left-0 w-full h-full pointer-events-auto z-[101]"
            style={{
              cursor,
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={(e) => {
              handleMouseMove(e);
              const point = screenToNormalized(e.clientX, e.clientY);
              // Check if hovering over a handle for cursor feedback
              if (!dragState && selectedBoxIndex !== null) {
                if (point) {
                  const handleIndex = findHandleAtPoint(
                    point,
                    selectedBoxIndex,
                  );
                  if (handleIndex !== null) {
                    setHoveredHandle({
                      boxIndex: selectedBoxIndex,
                      cornerIndex: handleIndex,
                    });
                  } else {
                    setHoveredHandle(null);
                  }
                }
              } else {
                setHoveredHandle(null);
              }
              // Track hovered box for product tooltip
              if (point && boxProductLabels?.size) {
                const boxAtPt = findBoxAtPoint(point);
                if (boxAtPt) {
                  const idx = boxAtPt.isUserDrawn
                    ? existingBoxes.length + boxAtPt.boxIndex
                    : boxAtPt.boxIndex;
                  if (boxProductLabels.has(idx)) {
                    setHoveredBoxIndex(idx);
                    // Position relative to the UNTRANSFORMED outer container so
                    // the tooltip isn't scaled/offset by zoom.
                    const rect = containerRef.current?.getBoundingClientRect();
                    if (rect) {
                      setHoverPos({
                        x: e.clientX - rect.left,
                        y: e.clientY - rect.top,
                      });
                    }
                  } else {
                    setHoveredBoxIndex(null);
                    setHoverPos(null);
                  }
                } else {
                  setHoveredBoxIndex(null);
                  setHoverPos(null);
                }
              }
              // Track hovered product-detection box (drives planogram highlight).
              // Suppressed while dragging/drawing so panning doesn't spuriously fire.
              if (onDetectionHoverRef.current && !dragState && !isDrawing) {
                const detectionIdx = point ? findDetectionAtPoint(point) : null;
                if (detectionIdx !== hoveredDetectionIndex) {
                  setHoveredDetectionIndex(detectionIdx);
                  onDetectionHoverRef.current(detectionIdx);
                }
                if (
                  detectionIdx !== null &&
                  detectionBoxLabels?.has(detectionIdx)
                ) {
                  const rect = containerRef.current?.getBoundingClientRect();
                  if (rect) {
                    setDetectionHoverPos({
                      x: e.clientX - rect.left,
                      y: e.clientY - rect.top,
                    });
                  }
                } else if (detectionHoverPos) {
                  setDetectionHoverPos(null);
                }
              }
            }}
            onMouseUp={handleMouseUp}
            onMouseLeave={() => {
              setHoveredHandle(null);
              setHoveredBoxIndex(null);
              setHoverPos(null);
              if (hoveredDetectionIndex !== null) {
                setHoveredDetectionIndex(null);
                setDetectionHoverPos(null);
                onDetectionHoverRef.current?.(null);
              }
              if (!dragState) {
                handleMouseUp();
              }
            }}
            onContextMenu={(e) => {
              if (enableDeletion) e.preventDefault();
            }}
          />
        )}
      </div>
    );
  }, [
    src,
    displaySrc,
    alt,
    crop,
    dragState,
    hoveredHandle,
    isDrawing,
    isDrawingMode,
    enableDrawing,
    enableDeletion,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    screenToNormalized,
    findHandleAtPoint,
    findBoxAtPoint,
    findDetectionAtPoint,
    selectedBoxIndex,
    hoveredBoxIndex,
    hoverPos,
    boxProductLabels,
    existingBoxes.length,
    detectionBoxLabels,
    hoveredDetectionIndex,
    detectionHoverPos,
  ]);

  if (!src) return null;

  // Tooltip product info, resolved from the hovered box/detection index.
  const hoverInfo =
    hoveredBoxIndex !== null ? boxProductLabels?.get(hoveredBoxIndex) : undefined;
  const detectionInfo =
    hoveredDetectionIndex !== null
      ? detectionBoxLabels?.get(hoveredDetectionIndex)
      : undefined;

  return (
    <Box
      ref={containerRef}
      className={`relative w-full h-full flex justify-center ${className}`}
      style={{
        aspectRatio: crop ? "1/1" : "auto",
        ...style,
      }}
    >
      {isLoading && <ImageLoading />}
      {enableZoom ? (
        <TransformWrapper
          ref={transformRef as React.Ref<ReactZoomPanPinchRef>}
          initialScale={initialZoom}
          minScale={minZoom}
          maxScale={4}
          wheel={
            holdKeysForZoom
              ? { step: 0.2, activationKeys: ["Control", "Meta"] }
              : { step: 0.2 }
          }
          pinch={{ step: 5 }}
          doubleClick={{ disabled: true }}
          panning={{
            velocityDisabled: !isDrawingMode && !dragState && !isDrawing,
          }}
          onInit={handleInit}
          onTransformed={handleTransformed}
        >
          <TransformComponent
            wrapperStyle={{ width: "100%", height: "100%" }}
            contentStyle={{ width: "100%", height: "100%" }}
          >
            {imageContainer}
          </TransformComponent>
        </TransformWrapper>
      ) : (
        imageContainer
      )}

      {/* Box draw mode controls */}
      {!!showModeControls && (
        <BoxDrawModeControls mode={mode} handleSetMode={handleSetMode} />
      )}

      {/* Hover tooltips — rendered outside the zoom transform so they stay
          pinned to the cursor at constant size. */}
      {hoverPos && hoverInfo && <HoverTooltip pos={hoverPos} info={hoverInfo} />}
      {detectionHoverPos && detectionInfo && (
        <HoverTooltip pos={detectionHoverPos} info={detectionInfo} />
      )}
    </Box>
  );
};
